import React, { useState, useEffect, useContext } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Button,
  Progress,
  Avatar,
  Space,
  Badge,
  Statistic,
  Tooltip,
  Modal,
  Form,
  Input,
  Select,
  message,
  Divider,
  Alert,
  Empty,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MessageOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  FireOutlined,
  TrophyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { AppContext } from "../context/AppContext";
import axios from "axios";
import moment from "moment";
import "./DueTodayDashboard.css";

const { Title, Text } = Typography;
const { Option } = Select;

const DueTodayDashboard = () => {
  const { company, user } = useContext(AppContext);
  const [dueLoans, setDueLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRemaining, setTimeRemaining] = useState("");
  const [quickActionModal, setQuickActionModal] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [dailyStats, setDailyStats] = useState({
    target: 10,
    collected: 3,
    totalAmount: 0,
    streak: 5,
  });
  const [form] = Form.useForm();

  const token = localStorage.getItem("token");

  // Real-time countdown to end of day
  useEffect(() => {
    const updateCountdown = () => {
      const now = moment();
      const endOfDay = moment().endOf('day');
      const duration = moment.duration(endOfDay.diff(now));
      
      if (duration.asMilliseconds() > 0) {
        setTimeRemaining(
          `${duration.hours()}h ${duration.minutes()}m ${duration.seconds()}s`
        );
      } else {
        setTimeRemaining("Day ended");
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchDueLoans = async () => {
    try {
      setLoading(true);
      const today = moment().format('YYYY-MM-DD');
      
      // First, let's get all active loans and then filter client-side for better debugging
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loans?filter[loan_status]=7&fields=*,borrower.first_name,borrower.last_name,borrower.phone_number,borrower.credit_score,loan_product.loan_name,loan_product.*&filter[company]=${company?.id}&sort=-next_payment_amount`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      console.log("All active loans:", response.data.data);
      console.log("Today's date:", today);

      // Filter loans that are due today (client-side filtering for reliability)
      const dueToday = response.data.data.filter(loan => {
        if (!loan.next_payment_date) return false;
        
        // Try multiple date format comparisons
        const paymentDate = moment(loan.next_payment_date).format('YYYY-MM-DD');
        const isToday = paymentDate === today;
        
        console.log(`Loan ${loan.id}: next_payment_date = ${loan.next_payment_date}, formatted = ${paymentDate}, isToday = ${isToday}`);
        
        return isToday;
      });

      console.log("Loans due today:", dueToday);

      const loans = dueToday.map(loan => ({
        ...loan,
        riskScore: calculateRiskScore(loan),
        priority: calculatePriority(loan),
      }));

      setDueLoans(loans);
      updateDailyStats(loans);
    } catch (error) {
      console.error("Error fetching due loans:", error);
      message.error("Failed to load due loans");
    } finally {
      setLoading(false);
    }
  };

  const calculateRiskScore = (loan) => {
    let score = 70; // Base score
    
    // Credit score impact
    if (loan.borrower?.credit_score) {
      const creditScore = loan.borrower.credit_score;
      if (creditScore > 750) score += 20;
      else if (creditScore > 650) score += 10;
      else if (creditScore < 500) score -= 30;
    }
    
    // Payment history impact
    const paymentProgress = (loan.paid_amount || 0) / loan.amount;
    if (paymentProgress > 0.5) score += 15;
    else if (paymentProgress < 0.2) score -= 20;
    
    // Loan amount impact (larger loans = higher risk)
    if (loan.amount > 50000) score -= 10;
    else if (loan.amount < 10000) score += 5;
    
    return Math.max(10, Math.min(95, score));
  };

  const calculatePriority = (loan) => {
    const riskScore = loan.riskScore || 50;
    const amount = loan.next_payment_amount || loan.amount * 0.1;
    
    if (riskScore < 40 || amount > 5000) return "high";
    if (riskScore < 60 || amount > 2000) return "medium";
    return "low";
  };

  const updateDailyStats = (loans) => {
    const totalAmount = loans.reduce((sum, loan) => sum + (loan.next_payment_amount || 0), 0);
    setDailyStats(prev => ({
      ...prev,
      totalAmount,
      target: loans.length,
    }));
  };

  const handleQuickAction = (loan, action) => {
    setSelectedLoan(loan);
    if (action === 'call') {
      // Trigger phone call
      window.open(`tel:${loan.borrower?.phone_number}`);
    } else if (action === 'sms') {
      setQuickActionModal(true);
    } else if (action === 'payment') {
      // Navigate to payment recording
      window.location.href = `/loan/${loan.id}`;
    }
  };

  const sendQuickSMS = async (values) => {
    try {
      // SMS sending logic here
      message.success(`SMS sent to ${selectedLoan.borrower?.first_name}`);
      setQuickActionModal(false);
      form.resetFields();
    } catch (error) {
      message.error("Failed to send SMS");
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "#ff4d4f";
      case "medium": return "#fa8c16";
      case "low": return "#52c41a";
      default: return "#1890ff";
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case "high": return <FireOutlined />;
      case "medium": return <WarningOutlined />;
      case "low": return <CheckCircleOutlined />;
      default: return <ClockCircleOutlined />;
    }
  };

  useEffect(() => {
    if (company?.id) {
      fetchDueLoans();
    }
  }, [company?.id]);

  if (loading) {
    return (
      <div className="due-today-loading">
        <div className="pulse-loader">
          <FireOutlined style={{ fontSize: 48, color: '#ff4d4f' }} />
          <Text>Loading today's urgent loans...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className="due-today-dashboard">
      {/* Header with Real-time Stats */}
      <Card className="dashboard-header" bordered={false}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={2} className="dashboard-title">
              <FireOutlined className="title-icon urgent-pulse" />
              Due Today Dashboard
            </Title>
            <Text type="secondary">
              {dueLoans.length} loans requiring immediate attention
            </Text>
          </Col>
          <Col>
            <Space size="large">
              <Card size="small" className="countdown-card">
                <Statistic
                  title="Time Remaining"
                  value={timeRemaining}
                  prefix={<ClockCircleOutlined />}
                  valueStyle={{ color: '#ff4d4f', fontSize: 16 }}
                />
              </Card>
              <Card size="small" className="streak-card">
                <Statistic
                  title="Collection Streak"
                  value={dailyStats.streak}
                  suffix="days"
                  prefix={<TrophyOutlined />}
                  valueStyle={{ color: '#faad14', fontSize: 16 }}
                />
              </Card>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Daily Progress */}
      <Card className="progress-card" bordered={false}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={8}>
            <div className="progress-item">
              <Text strong>Daily Collection Progress</Text>
              <Progress
                percent={Math.round((dailyStats.collected / dailyStats.target) * 100)}
                strokeColor={{
                  from: '#108ee9',
                  to: '#87d068',
                }}
                trailColor="#f0f0f0"
                strokeWidth={8}
              />
              <Text type="secondary">
                {dailyStats.collected} of {dailyStats.target} loans collected
              </Text>
            </div>
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Target Amount Today"
              value={dailyStats.totalAmount}
              prefix={<DollarOutlined />}
              suffix="ZMW"
              valueStyle={{ color: '#3f8600' }}
            />
          </Col>
          <Col xs={24} sm={8}>
            <Statistic
              title="Priority Actions"
              value={dueLoans.filter(loan => loan.priority === 'high').length}
              prefix={<FireOutlined />}
              suffix="urgent"
              valueStyle={{ color: '#cf1322' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Loan Cards */}
      {dueLoans.length === 0 ? (
        <Card className="empty-state">
          <Empty
            image={<CheckCircleOutlined style={{ fontSize: 64, color: '#52c41a' }} />}
            description={
              <div>
                <Title level={4} style={{ color: '#52c41a' }}>
                  🎉 All Caught Up!
                </Title>
                <Text>No loans due today. Great work!</Text>
              </div>
            }
          />
        </Card>
      ) : (
        <Row gutter={[16, 16]}>
          {dueLoans.map((loan, index) => (
            <Col xs={24} sm={12} md={8} key={loan.id}>
              <Badge.Ribbon
                text={loan.priority.toUpperCase()}
                color={getPriorityColor(loan.priority)}
              >
                <Card
                  className={`due-loan-card priority-${loan.priority} ${
                    loan.priority === 'high' ? 'urgent-pulse' : ''
                  }`}
                  hoverable
                  actions={[
                    <Tooltip title="Call Borrower">
                      <Button
                        type="text"
                        icon={<PhoneOutlined />}
                        onClick={() => handleQuickAction(loan, 'call')}
                        className="action-button call-button"
                      />
                    </Tooltip>,
                    <Tooltip title="Send SMS">
                      <Button
                        type="text"
                        icon={<MessageOutlined />}
                        onClick={() => handleQuickAction(loan, 'sms')}
                        className="action-button sms-button"
                      />
                    </Tooltip>,
                    <Tooltip title="Record Payment">
                      <Button
                        type="text"
                        icon={<DollarOutlined />}
                        onClick={() => handleQuickAction(loan, 'payment')}
                        className="action-button payment-button"
                      />
                    </Tooltip>,
                  ]}
                >
                  <div className="loan-header">
                    <Avatar
                      size={48}
                      icon={<UserOutlined />}
                      style={{
                        backgroundColor: getPriorityColor(loan.priority),
                      }}
                    />
                    <div className="borrower-info">
                      <Title level={5} className="borrower-name">
                        {loan.borrower?.first_name} {loan.borrower?.last_name}
                      </Title>
                      <Text type="secondary" className="loan-id">
                        Loan #{loan.id}
                      </Text>
                      <div className="risk-indicator">
                        <Progress
                          percent={loan.riskScore}
                          size="small"
                          strokeColor={
                            loan.riskScore > 70 ? '#52c41a' : 
                            loan.riskScore > 40 ? '#faad14' : '#ff4d4f'
                          }
                          format={() => `${loan.riskScore}% Safe`}
                        />
                      </div>
                    </div>
                  </div>

                  <Divider style={{ margin: '12px 0' }} />

                  <div className="loan-details">
                    <Row gutter={[8, 8]}>
                      <Col span={12}>
                        <Text strong style={{ color: getPriorityColor(loan.priority) }}>
                          ZMW {(loan.next_payment_amount || 0).toLocaleString()}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Due Amount
                        </Text>
                      </Col>
                      <Col span={12}>
                        <Text strong>
                          {loan.borrower?.phone_number || 'No phone'}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Contact
                        </Text>
                      </Col>
                      <Col span={24}>
                        <div className="payment-progress">
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Loan Progress
                          </Text>
                          <Progress
                            percent={Math.round(((loan.paid_amount || 0) / loan.amount) * 100)}
                            size="small"
                            strokeColor={getPriorityColor(loan.priority)}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className="loan-tags">
                    <Tag icon={getPriorityIcon(loan.priority)} color={getPriorityColor(loan.priority)}>
                      {loan.priority.toUpperCase()} PRIORITY
                    </Tag>
                    {loan.loan_product?.loan_name && (
                      <Tag color="blue">{loan.loan_product.loan_name}</Tag>
                    )}
                  </div>
                </Card>
              </Badge.Ribbon>
            </Col>
          ))}
        </Row>
      )}

      {/* Quick Action Modal */}
      <Modal
        title={
          <div>
            <MessageOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Send Quick SMS to {selectedLoan?.borrower?.first_name}
          </div>
        }
        open={quickActionModal}
        onCancel={() => setQuickActionModal(false)}
        onOk={() => form.submit()}
        okText="Send SMS"
        okButtonProps={{ icon: <MessageOutlined /> }}
      >
        <Form form={form} onFinish={sendQuickSMS} layout="vertical">
          <Alert
            message={`Sending to: ${selectedLoan?.borrower?.phone_number}`}
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form.Item
            name="template"
            label="Message Template"
            rules={[{ required: true, message: "Please select a template" }]}
          >
            <Select placeholder="Choose a quick template">
              <Option value="reminder">
                Payment Reminder: "Hi {selectedLoan?.borrower?.first_name}, your loan payment of ZMW {selectedLoan?.next_payment_amount} is due today. Please make your payment to avoid late fees."
              </Option>
              <Option value="friendly">
                Friendly Reminder: "Hello! Just a friendly reminder that your loan payment is due today. Thank you for being a valued customer!"
              </Option>
              <Option value="urgent">
                Urgent Notice: "URGENT: Your loan payment is overdue. Please contact us immediately to avoid additional charges."
              </Option>
              <Option value="custom">Custom Message</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="customMessage"
            label="Custom Message"
            style={{ display: form.getFieldValue('template') === 'custom' ? 'block' : 'none' }}
          >
            <Input.TextArea
              rows={4}
              placeholder="Type your custom message here..."
              maxLength={160}
              showCount
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DueTodayDashboard;